import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'

import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  contracts: [],
  show_form: false,
  current_contract: {
    id: null,
    dealer_brand_id: null,
    appointment: null,
    dealer_brand: null,
    sig_name: '',
    status: '',
  },
  appointments: { total: 0, appointments: [] },
  pending: false,
}

export const getContracts = createAsyncThunk(
  'contracts/list',
  async ({ offset, limit, sortData, filter, filtered_brands, filtered_users, filtered_statuses }, thunkApi) => {
    try {
      let { total: currentTotal } = thunkApi.getState().contracts
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/contracts`,
        params: {
          offset,
          limit,
          sort_column,
          sort_direction,
          filter,
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          filtered_statuses: filtered_statuses.join(','),
        },
      })
      let { total, contracts } = response.data
      return { total, contracts, reset: offset === 0 }
    } catch (err) {
      error(t('store.contracts.get_contracts_error', 'Impossibile recuperare lista contratti'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const createContract = createAsyncThunk('contracts/create', async ({ contract }, thunkApi) => {
  try {
    await axios({
      url: `/api/contracts`,
      method: 'post',
      data: { ...contract },
    })
    success(t('store.contracts.create_contract_success', 'Contratto creato con successo'))
    thunkApi.dispatch(resetCurrentContract())
    if (thunkApi.getState().contracts.show_form) {
      thunkApi.dispatch(toggleShowForm())
    }
  } catch (err) {
    error(t('store.contracts.create_contract_error', 'Impossibile creare il contratto'))
    thunkApi.dispatch(resetCurrentContract())
    if (thunkApi.getState().contracts.show_form) {
      thunkApi.dispatch(toggleShowForm())
    }
    return thunkApi.rejectWithValue()
  }
})

export const updateContract = createAsyncThunk('contracts/update', async ({ contract }, thunkApi) => {
  try {
    await axios({
      url: `/api/contracts/${contract.id}`,
      method: 'patch',
      data: { ...contract },
    })
    success(t('store.contracts.update_contract_success', 'Contratto aggiornato con successo'))
    thunkApi.dispatch(resetCurrentContract())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    error(t('store.contracts.update_contract_error', 'Impossibile aggiornare il contratto'))
    thunkApi.dispatch(resetCurrentContract())
    thunkApi.dispatch(toggleShowForm())
    return thunkApi.rejectWithValue()
  }
})

export const deleteContract = createAsyncThunk('contracts/delete', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/contracts/${id}`, method: 'delete' })
    success(t('store.contracts.delete_contract_success', 'Contratto eliminato con successo'))
    thunkApi.dispatch(resetCurrentContract())
  } catch (err) {
    error(t('store.contracts.delete_contract_error', 'Impossibile eliminare il contratto'))
    return thunkApi.rejectWithValue()
  }
})

export const changeStatusContract = createAsyncThunk(
  'contracts/changeStatus',
  async ({ id, status, date }, thunkApi) => {
    try {
      await axios({ url: `/api/contracts/${id}/status`, method: 'patch', data: { status, date } })
      success(t('store.contracts.change_status_success', 'Stato cambiato con successo'))
    } catch (err) {
      error(t('store.contracts.change_status_error', 'Impossibile cambiare lo stato'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getAppointments = createAsyncThunk(
  'contacts/appoinmentsList',
  async (
    {
      offset,
      limit,
      sortData,
      filter,
      only_without_contracts = true,
      filtered_brands,
      filtered_users,
      filtered_statuses,
      filtered_types = [],
    },
    thunkApi
  ) => {
    try {
      let { total: currentTotal } = thunkApi.getState().contracts.appointments
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/appointments`,
        params: {
          offset,
          limit,
          sort_column,
          sort_direction,
          filter,
          only_without_contracts,
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          filtered_statuses: filtered_statuses.join(','),
          filtered_types: filtered_types.join(','),
        },
      })
      let { total, appointments } = response.data
      return { total, appointments, reset: offset === 0 }
    } catch (err) {
      error(t('store.contracts.get_appointments_error', 'Impossibile recuperare lista visite'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const contractsSlice = createSlice({
  name: 'contracts',
  initialState,

  reducers: {
    setCurrentContract: (state, action) => {
      let { id, dealer_brand_id, appointment, sig_name, status } = action.payload
      state.current_contract.id = id
      state.current_contract.dealer_brand_id = dealer_brand_id
      state.current_contract.appointment = appointment
      state.current_contract.sig_name = sig_name
      state.current_contract.status = status
    },
    toggleShowForm: state => {
      state.show_form = !state.show_form
    },
    resetCurrentContract: state => {
      state.current_contract = initialState.current_contract
    },
    resetContractAppointments: state => {
      state.appointments = initialState.appointments
    },
    resetContracts: () => initialState,
  },

  extraReducers: {
    [getContracts.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, contracts } = action.payload
      state.total = total
      state.contracts = action.payload.reset ? [...contracts] : [...state.contracts, ...contracts]
    },
    [getContracts.rejected]: state => {
      state.total = initialState.total
      state.contracts = initialState.contracts
    },
    [createContract.fulfilled]: state => {
      state.pending = false
    },
    [createContract.rejected]: state => {
      state.pending = false
    },
    [createContract.pending]: state => {
      state.pending = true
    },
    [updateContract.fulfilled]: state => {
      state.pending = false
    },
    [updateContract.rejected]: state => {
      state.pending = false
    },
    [updateContract.pending]: state => {
      state.pending = true
    },
    [deleteContract.fulfilled]: state => {
      state.pending = false
    },
    [deleteContract.rejected]: state => {
      state.pending = false
    },
    [deleteContract.pending]: state => {
      state.pending = true
    },
    [getAppointments.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, appointments } = action.payload
      state.appointments.total = total
      state.appointments.appointments = action.payload.reset
        ? [...appointments]
        : [...state.appointments.appointments, ...appointments]
    },
    [getAppointments.rejected]: state => {
      state.appointments = initialState.appointments
    },
    [changeStatusContract.fulfilled]: state => {
      state.pending = false
    },
    [changeStatusContract.rejected]: state => {
      state.pending = false
    },
    [changeStatusContract.pending]: state => {
      state.pending = true
    },
  },
})

export const { setCurrentContract, resetCurrentContract, toggleShowForm, resetContracts, resetContractAppointments } =
  contractsSlice.actions
export default contractsSlice.reducer
